<template>
  <div
    class="admin-table-card admin-card admin-card--table"
    :style="{ width: '100%' }"
    v-loading="loading"
    :element-loading-text="t('loading')"
  >
    <div class="admin-card__body">
      <template v-if="loading && !tableData?.length">
        <div class="table-skeleton" aria-hidden="true">
          <el-skeleton animated>
            <template #template>
              <div class="table-skeleton__head">
                <el-skeleton-item
                  v-for="n in skeletonCols"
                  :key="n"
                  variant="text"
                  class="table-skeleton__cell"
                />
              </div>
              <el-skeleton-item
                v-for="row in skeletonRows"
                :key="row"
                variant="p"
                class="table-skeleton__row"
              />
            </template>
          </el-skeleton>
        </div>
      </template>
      <el-table
        v-show="!loading || tableData?.length"
        :data="tableData"
        :style="{ width: '100%', 'overflow-x': 'auto' }"
        :height="offsetHeight"
        flexible
        ref="tableRef"
        fil
        clearSort
        v-bind="$attrs"
        table-layout="auto"
        @selection-change="handleSelectionChange"
      >
        <el-table-column
          v-for="item in column"
          :type="item.type"
          :key="item.prop || item.type"
          v-bind="item"
          :width="item.width"
          :fixed="item.prop == 'operation' ? 'right' : false"
          :sortable="item.sortable ?? false"
          :show-overflow-tooltip="item.operation === 'operation' ? false : item.showOverflowTooltip ?? true"
        >
          <template #default="scope" v-if="$slots[item.prop]">
            <slot :name="item.prop" v-bind="scope"> </slot>
          </template>
          <template #default="scope" v-else-if="isDateColumnProp(item.prop)">
            {{ formatDisplayDateTime(scope.row[item.prop]) || '-' }}
          </template>
        </el-table-column>
        <template v-slot:empty>
          <hc-empty :image-size="120" />
        </template>
      </el-table>
      <div v-if="!isHidePage && !loading" class="pages">
        <el-config-provider :locale="zhCn">
          <el-pagination
            v-model:current-page="currentPage4"
            v-model:page-size="pageSize4"
            background
            :page-sizes="[20, 30, 50, 100]"
            :small="small"
            :disabled="disabled"
            :total="maxPage"
            @size-change="handleSizeChange"
            layout="total, sizes, prev, pager, next, jumper"
            @current-change="handleCurrentChange"
          />
        </el-config-provider>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from "vue";
import zhCn from "element-plus/dist/locale/zh-cn.mjs";
import hcEmpty from "@/components/hc-empty/index.vue";
import { t } from "@/locale/index.js";
import { formatDisplayDateTime, isDateColumnProp } from "@/utils/dateTime.js";

const emit = defineEmits('pageChange', 'limitChange', 'selectionChange');

const currentPage4 = ref(1);
const disabled = ref(false);
const small = ref(false);
const pageSize4 = ref(20);

const props = defineProps({
  offsetHeight: { type: Number },
  column: { type: Array, default: () => [] },
  tableData: { type: Array, default: () => [] },
  maxPage: { type: Number, default: 0 },
  isHidePage: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
});

const skeletonCols = computed(() => Math.min(Math.max(props.column?.length || 5, 4), 8));
const skeletonRows = 6;

const handleCurrentChange = () => {
  emit('pageChange', currentPage4.value);
};

const handleSizeChange = () => {
  emit('limitChange', pageSize4.value);
};

const handleSelectionChange = (selection) => {
  emit('selectionChange', selection);
};
</script>

<style lang="less" scoped>
.admin-card__body {
  padding: var(--admin-card-padding, 16px 20px);
  position: relative;
  min-height: 120px;
}

.table-skeleton {
  padding: 4px 0;
}

.table-skeleton__head {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.table-skeleton__cell {
  flex: 1;
  height: 20px;
}

.table-skeleton__row {
  margin-bottom: 14px;
  height: 16px;
}

.pages {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
  align-items: center;
}
</style>
